import { Injectable } from '@angular/core';
import { ConfigurationService } from './configuration.service';

export interface CompanyConfig {
  name: string;
  phone: {
    display: string;
    tel: string;
  };
  email: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    full: string;
  };
  serviceAreas: string[];
  hours: {
    weekdays: string;
    saturday: string;
    sunday: string;
  };
  social: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CompanyConfigService {
  private configService = new ConfigurationService();
  
  // Main config for backward compatibility
  private config: CompanyConfig = {
    name: 'Complete Pest Control',
    phone: {
      display: '(801) 918-3014',
      tel: '+18019183014'
    },
    email: 'service@completepestcontrol.pro',
    address: {
      street: '681 White Horse Dr',
      city: 'Spanish Fork',
      state: 'UT',
      zip: '84660',
      full: '681 White Horse Dr, Spanish Fork, UT 84660'
    },
    serviceAreas: [
      'South Ogden', 
      'North Ogden', 
      'Riverdale', 
      'Roy', 
      'West Haven', 
      'Marriott-Slaterville', 
      'Harrisville', 
      'Pleasant View', 
      'Hooper', 
      'Plain City', 
      'Uintah', 
      'Washington Terrace', 
      'Huntsville', 
      'Layton', 
      'Clearfield', 
      'Kaysville', 
      'Clinton', 
      'Sunset', 
      'Syracuse', 
      'West Point', 
      'Fruit Heights',
      'Brigham City', 
      'Perry', 
      'Willard'
    ],
    hours: {
      weekdays: '8:00 AM - 6:00 PM',
      saturday: '9:00 AM - 4:00 PM',
      sunday: 'Emergency calls only'
    },
    social: {
      facebook: 'https://www.facebook.com/completepestcontrol.pro',
      instagram: 'https://www.instagram.com/completepestcontrol.pro',
    }
  };

  // Cached config from configuration system
  private configFromSystem: CompanyConfig | null = null;
  private configLoadTime: Date | null = null;

  /**
   * Get configuration - tries to load from configuration system, falls back to hardcoded
   */
  getConfig(): CompanyConfig {
    // Return hardcoded config for immediate use (backward compatibility)
    return this.config;
  }

  /**
   * Get configuration from the configuration system (async)
   * This is the new method that uses the configuration system
   */
  async getConfigFromSystem(): Promise<CompanyConfig> {
    try {
      // Check if we need to reload config
      const now = new Date();
      const cacheExpired = !this.configLoadTime || 
        (now.getTime() - this.configLoadTime.getTime()) > 5 * 60 * 1000; // 5 minutes

      if (!this.configFromSystem || cacheExpired) {
        const companyInfo = await this.configService.getCompanyInfo();
        
        this.configFromSystem = {
          name: companyInfo.name,
          phone: {
            display: companyInfo.phoneDisplay,
            tel: companyInfo.phoneTel
          },
          email: companyInfo.email,
          address: companyInfo.address,
          serviceAreas: this.config.serviceAreas, // Keep service areas from fallback for now
          hours: companyInfo.hours,
          social: companyInfo.social
        };
        
        this.configLoadTime = now;
      }
      
      return this.configFromSystem;
    } catch (error) {
      console.warn('⚠️ Could not load company configuration, using fallback:', error);
      return this.config;
    }
  }

  getPhone(): { display: string; tel: string } {
    return this.config.phone;
  }

  async getPhoneFromSystem(): Promise<{ display: string; tel: string }> {
    const config = await this.getConfigFromSystem();
    return config.phone;
  }

  getEmail(): string {
    return this.config.email;
  }

  async getEmailFromSystem(): Promise<string> {
    const config = await this.getConfigFromSystem();
    return config.email;
  }

  getAddress(): CompanyConfig['address'] {
    return this.config.address;
  }

  async getAddressFromSystem(): Promise<CompanyConfig['address']> {
    const config = await this.getConfigFromSystem();
    return config.address;
  }

  getServiceAreas(): string[] {
    return this.config.serviceAreas;
  }

  getHours(): CompanyConfig['hours'] {
    return this.config.hours;
  }

  async getHoursFromSystem(): Promise<CompanyConfig['hours']> {
    const config = await this.getConfigFromSystem();
    return config.hours;
  }

  getSocial(): CompanyConfig['social'] {
    return this.config.social;
  }

  async getSocialFromSystem(): Promise<CompanyConfig['social']> {
    const config = await this.getConfigFromSystem();
    return config.social;
  }

  // Helper methods for common actions (backward compatible)
  callPhone(): void {
    window.location.href = `tel:${this.config.phone.tel}`;
  }

  sendEmail(): void {
    window.location.href = `mailto:${this.config.email}`;
  }

  openDirections(): void {
    const address = encodeURIComponent(this.config.address.full);
    window.open(`https://maps.google.com/?q=${address}`, '_blank');
  }

  openSocialLink(platform: keyof CompanyConfig['social']): void {
    const url = this.config.social[platform];
    if (url) {
      window.open(url, '_blank');
    }
  }

  // Async versions for new components
  async callPhoneFromSystem(): Promise<void> {
    const phone = await this.getPhoneFromSystem();
    window.location.href = `tel:${phone.tel}`;
  }

  async sendEmailFromSystem(): Promise<void> {
    const email = await this.getEmailFromSystem();
    window.location.href = `mailto:${email}`;
  }

  async openDirectionsFromSystem(): Promise<void> {
    const address = await this.getAddressFromSystem();
    const addressString = encodeURIComponent(address.full);
    window.open(`https://maps.google.com/?q=${addressString}`, '_blank');
  }

  async openSocialLinkFromSystem(platform: keyof CompanyConfig['social']): Promise<void> {
    const social = await this.getSocialFromSystem();
    const url = social[platform];
    if (url) {
      window.open(url, '_blank');
    }
  }
}